import { CHA_PRODUCT_FILTER_OPTIONS } from '@/utils/chaProducts'

/*
Lo que comparten las dos versiones del anuncio, escritorio y teléfono: el texto
de los pasos y los datos de mentira que recorren. Vive en un solo lugar para
que las dos cuenten exactamente lo mismo.
*/

// Igual que ROLE.EXTERNAL_PRODUCTS y ROLE_HINT.EXTERNAL_PRODUCTS en
// public/locales/es/enums.json, que es lo que muestra el perfil real.
export const ROLE_LABEL = 'Aplicar destacados en ChileAutos'
export const ROLE_HINT =
  'Usa cupos de TopSpot, Showcase y TopNitro, que no se reembolsan. Premium se libera al quitarlo.'

export const STEPS: { number: 1 | 2 | 3; title: string; first?: boolean; text: string }[] = [
  {
    number: 1,
    title: 'Activa el permiso',
    first: true,
    text: `Nadie tiene el permiso por defecto. Un administrador tiene que habilitar el permiso “${ROLE_LABEL}” en Usuarios. Esa persona lo verá al volver a iniciar sesión.`,
  },
  {
    number: 2,
    title: 'Aplica y quita destacados',
    text: 'En la pestaña Destacados de cada aviso publicado en ChileAutos ves lo que tiene el aviso, los días que le quedan y tus cupos. TopSpot, Showcase y TopNitro usan cupos que no se reembolsan, así que Veekls pide confirmación antes.',
  },
  {
    number: 3,
    title: 'Encuentra tus destacados',
    text: 'Cada aviso destacado lleva su etiqueta en Publicaciones. Al elegir ChileAutos en Plataformas aparece el filtro Destacado, para ver solo los Premium, TopSpot, Showcase o TopNitro.',
  },
]

export const NOTE =
  'Veekls también te avisa antes de despublicar, archivar o vender un vehículo con un destacado activo. Los destacados que apliques desde el portal de ChileAutos no se ven en Veekls.'

export const TEAM = [
  { name: 'Marcelo Fuentes', job: 'Administrador', state: 'Activo', primary: false },
  { name: 'Camilo Rojas', job: 'Vendedor', state: 'Activo', primary: true },
  { name: 'Diego Salas', job: 'Vendedor', state: 'Activo', primary: false },
  { name: 'Ignacio Bravo', job: 'Jefe de ventas', state: 'Activo', primary: false },
]

/**
 * The profile's permissions, in the order the real screen renders them.
 *
 * `UserEdit` maps over the role enum, so the new one lands right after
 * "Publicar en plataformas externas"; the mock keeps that neighbour because it
 * is how someone will find it. `tone` dims the ones far from it.
 */
export const PERMISSIONS: { label: string; checked: boolean; tone: 'far' | 'near' | 'focus'; isTarget?: boolean }[] = [
  { label: 'Gestionar vehículos', checked: true, tone: 'far' },
  { label: 'Modificar precio de vehículos', checked: true, tone: 'far' },
  { label: 'Archivar y restaurar vehículos', checked: false, tone: 'far' },
  { label: 'Modificar compras', checked: false, tone: 'far' },
  { label: 'Gestionar cotizaciones propias', checked: true, tone: 'near' },
  { label: 'Gestionar promesas propias', checked: true, tone: 'near' },
  { label: 'Gestionar reservas propias', checked: true, tone: 'near' },
  { label: 'Publicar en plataformas externas', checked: true, tone: 'focus' },
  { label: ROLE_LABEL, checked: false, tone: 'focus', isTarget: true },
  { label: 'Gestionar control de ventas', checked: false, tone: 'near' },
  { label: 'Ver clientes', checked: true, tone: 'near' },
  { label: 'Ver miembros', checked: false, tone: 'near' },
  { label: 'Ver pagarés', checked: false, tone: 'far' },
  { label: 'Ver precio de compra', checked: false, tone: 'far' },
  { label: 'Ver vehículos', checked: true, tone: 'far' },
  { label: 'Anular consignaciones', checked: false, tone: 'far' },
]

export const toneClass = { far: 'opacity-30', near: 'opacity-60', focus: '' } as const

type Platform = 'Chileautos' | 'MercadoLibre'

/**
 * The listing. `hiddenBy` says which filter folds the row away: ChileAutos in
 * Plataformas hides the ones not published there, TopSpot in Destacado the rest.
 */
export const VEHICLES: {
  plate: string
  model: string
  year: string
  platforms: Platform[]
  star?: string
  isTarget?: boolean
  hiddenBy?: 'platform' | 'highlight'
}[] = [
  { plate: 'RZSF16', model: 'Hyundai Tucson', year: '2022', platforms: ['MercadoLibre'], hiddenBy: 'platform' },
  { plate: 'LDCD96', model: 'FIAT 500X', year: '2019', platforms: ['Chileautos'], isTarget: true },
  {
    plate: 'JXXF63',
    model: 'Volvo XC90',
    year: '2018',
    platforms: ['Chileautos'],
    star: 'Premium',
    hiddenBy: 'highlight',
  },
  { plate: 'SV5664', model: 'Land Rover Discovery', year: '2019', platforms: ['MercadoLibre'], hiddenBy: 'platform' },
  { plate: 'DLPV63', model: 'Citroën C3', year: '2021', platforms: ['Chileautos'], hiddenBy: 'highlight' },
]

export const PLATFORM_BADGE: Record<Platform, string> = {
  Chileautos: 'bg-[#e6f0ff] text-[#1f5fc4]',
  MercadoLibre: 'bg-[#fff6c2] text-[#8a6d00]',
}

export const STAR_BADGE = 'bg-[#fff4cf] text-[#a16207]'

export const PLATFORM_OPTIONS = ['MercadoLibre', 'ChileAutos', 'Yapo']

// The real Destacado options, so the mock lists exactly what the filter offers.
export const HIGHLIGHT_OPTIONS = Object.entries(CHA_PRODUCT_FILTER_OPTIONS).map(([value, label]) => ({ value, label }))

export const CONFIRM_MESSAGE =
  'Esto usa 1 de tus 3 TopSpot. Dura 7 días y no se reembolsa si vendes o despublicas el vehículo. ¿Continuar?'

export const isRowHidden = (
  hiddenBy: 'platform' | 'highlight' | undefined,
  state: { platformChosen: boolean; highlightChosen: boolean }
) => (hiddenBy === 'platform' && state.platformChosen) || (hiddenBy === 'highlight' && state.highlightChosen)
